import React from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../ui/card";
import { BehaviorRecommendation } from "../../types/tradingJournal";
import {
  BrainCircuit,
  CheckCircleIcon,
  AlertTriangleIcon,
  BadgeInfoIcon,
  TrendingUp,
  Clock,
  Shield,
} from "lucide-react";

interface BehaviorRecommendationsCardProps {
  behaviorRecommendations: BehaviorRecommendation[];
  onAddEntry: () => void;
}

const BehaviorRecommendationsCard = ({
  behaviorRecommendations,
  onAddEntry,
}: BehaviorRecommendationsCardProps) => {
  // Get icon based on recommendation title
  const getIconForTitle = (title: string) => {
    if (title.includes("Emotional") || title.includes("Calm"))
      return <Shield className="h-5 w-5" />;
    if (
      title.includes("Plan") ||
      title.includes("Consistency") ||
      title.includes("Discipline")
    )
      return <CheckCircleIcon className="h-5 w-5" />;
    if (
      title.includes("Time") ||
      title.includes("2PM") ||
      title.includes("Morning")
    )
      return <Clock className="h-5 w-5" />;
    if (
      title.includes("Cutting") ||
      title.includes("Loss") ||
      title.includes("Return")
    )
      return <TrendingUp className="h-5 w-5" />;
    return <BadgeInfoIcon className="h-5 w-5" />;
  };

  return (
    <Card className="intellectia-card h-full flex flex-col">
      <CardHeader>
        <CardTitle>Your Trading Recommendations</CardTitle>
        <CardDescription>
          Data-driven insights to improve your trading
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1">
        <div className="space-y-4">
          {behaviorRecommendations.map((rec, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border ${
                rec.type === "positive"
                  ? "border-green-500/30 bg-green-950/20"
                  : rec.type === "negative"
                  ? "border-red-500/30 bg-red-950/20"
                  : "border-blue-500/30 bg-blue-950/20"
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`p-2 rounded-full ${
                    rec.type === "positive"
                      ? "bg-green-900/30 text-green-400"
                      : rec.type === "negative"
                      ? "bg-red-900/30 text-red-400"
                      : "bg-blue-900/30 text-blue-400"
                  }`}
                >
                  {rec.type === "positive" ? (
                    getIconForTitle(rec.title)
                  ) : rec.type === "negative" ? (
                    <AlertTriangleIcon className="h-5 w-5" />
                  ) : (
                    <BadgeInfoIcon className="h-5 w-5" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-white">{rec.title}</h3>
                  <p className="text-white/70 text-sm mt-1">
                    {rec.description}
                  </p>
                  <p className="text-white/50 text-xs mt-2">{rec.impact}</p>
                </div>
              </div>
            </div>
          ))}

          {behaviorRecommendations.length === 0 && (
            <div className="text-center py-8 text-white/60">
              <BrainCircuit className="h-12 w-12 mx-auto text-white/30 mb-3" />
              <p>
                Add more journal entries to generate personalized
                recommendations.
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BehaviorRecommendationsCard;
